import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DAYS, ORIENTATIONS } from "@/lib/format";
import type { DisplayConfig, Playlist, ScheduleSlot } from "@/lib/types";
import { ScheduleTimeline } from "@/components/app/ScheduleTimeline";
import { PowerScheduleEditor } from "@/components/app/PowerScheduleEditor";
import { BannerStyleEditor } from "@/components/app/BannerStyleEditor";
import { BannerTemplates } from "@/components/app/BannerTemplates";
import { LayoutEditor } from "@/components/app/LayoutEditor";

interface Props {
  value: DisplayConfig;
  onChange: (c: DisplayConfig) => void;
  playlists: Playlist[];
  disabled?: boolean;
}

const NONE = "__none__";

export function PlaylistSelect({ value, onChange, playlists, testId, allowNone = true, disabled }: { value: string | null; onChange: (v: string | null) => void; playlists: Playlist[]; testId: string; allowNone?: boolean; disabled?: boolean }) {
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)} disabled={disabled}>
      <SelectTrigger data-testid={testId} className="border-slate-700 bg-slate-950/60"><SelectValue placeholder="Choisir une liste" /></SelectTrigger>
      <SelectContent className="border-slate-800 bg-slate-900">
        {allowNone && <SelectItem value={NONE}>Aucune</SelectItem>}
        {playlists.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SlotRow({ slot, index, playlists, onChange, onRemove, disabled }: { slot: ScheduleSlot; index: number; playlists: Playlist[]; onChange: (s: ScheduleSlot) => void; onRemove: () => void; disabled?: boolean }) {
  const toggleDay = (d: number) => {
    const days = slot.days.includes(d) ? slot.days.filter((x) => x !== d) : [...slot.days, d].sort();
    if (days.length) onChange({ ...slot, days });
  };
  return (
    <div data-testid={`schedule-slot-${index}`} className="grid gap-3 rounded-lg border border-slate-800 bg-slate-950/40 p-4 lg:grid-cols-[1.4fr_2fr_auto_auto_auto_auto] lg:items-end">
      <div><Label className="eyebrow">Liste</Label><PlaylistSelect value={slot.playlist_id} onChange={(v) => v && onChange({ ...slot, playlist_id: v })} playlists={playlists} allowNone={false} testId={`schedule-slot-${index}-playlist`} disabled={disabled} /></div>
      <div>
        <Label className="eyebrow">Jours</Label>
        <div className="mt-1 flex flex-wrap gap-1">
          {DAYS.map((d) => (
            <button key={d.v} type="button" disabled={disabled} data-testid={`schedule-slot-${index}-day-${d.v}`} onClick={() => toggleDay(d.v)}
              className={`h-9 w-11 rounded-md border text-xs font-semibold transition-colors ${slot.days.includes(d.v) ? "border-cyan-500/50 bg-cyan-500/15 text-cyan-300" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
              {d.short}
            </button>
          ))}
        </div>
      </div>
      <div><Label className="eyebrow">De</Label><Input type="time" disabled={disabled} data-testid={`schedule-slot-${index}-from`} value={slot.from_time} onChange={(e) => onChange({ ...slot, from_time: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
      <div><Label className="eyebrow">À</Label><Input type="time" disabled={disabled} data-testid={`schedule-slot-${index}-to`} value={slot.to_time} onChange={(e) => onChange({ ...slot, to_time: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
      <div><Label className="eyebrow">Priorité</Label><Input type="number" min={0} max={100} disabled={disabled} data-testid={`schedule-slot-${index}-priority`} value={slot.priority} onChange={(e) => onChange({ ...slot, priority: Number(e.target.value) || 0 })} className="w-20 border-slate-700 bg-slate-950/60" /></div>
      <Button type="button" variant="ghost" size="icon" disabled={disabled} data-testid={`schedule-slot-${index}-remove`} onClick={onRemove} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

export function ConfigEditor({ value, onChange, playlists, disabled }: Props) {
  const set = (patch: Partial<DisplayConfig>) => onChange({ ...value, ...patch });
  const setSlot = (i: number, s: ScheduleSlot) => set({ schedules: value.schedules.map((x, j) => (j === i ? s : x)) });
  const addSlot = () => playlists[0] && set({ schedules: [...value.schedules, { playlist_id: playlists[0].id, days: [1, 2, 3, 4, 5], from_time: "09:00", to_time: "17:00", priority: 0 }] });

  return (
    <div className="space-y-8" data-testid="config-editor">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Liste de lecture par défaut</Label>
          <PlaylistSelect value={value.default_playlist_id} onChange={(v) => set({ default_playlist_id: v })} playlists={playlists} testId="config-default-playlist" disabled={disabled} />
          <p className="text-xs text-slate-500">Jouée en dehors des plages planifiées.</p>
        </div>
        <div className="space-y-2">
          <Label>Couleur de fond de secours</Label>
          <div className="flex gap-2">
            <input type="color" disabled={disabled} data-testid="config-bg-color-picker" value={value.bg} onChange={(e) => set({ bg: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border border-slate-700 bg-transparent" />
            <Input disabled={disabled} data-testid="config-bg-color-input" value={value.bg} onChange={(e) => set({ bg: e.target.value })} className="font-mono border-slate-700 bg-slate-950/60" />
          </div>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Bandeau texte défilant</Label>
          <Textarea disabled={disabled} data-testid="config-banner-input" value={value.banner} maxLength={500} onChange={(e) => set({ banner: e.target.value })} placeholder="Ex. : Remise de 15 % sur la boutique aujourd'hui !" className="border-slate-700 bg-slate-950/60" />
          <BannerTemplates value={value.banner_style} onApply={(s) => set({ banner_style: s })} disabled={disabled} />
          <BannerStyleEditor value={value.banner_style} onChange={(s) => set({ banner_style: s })} text={value.banner} disabled={disabled} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 md:col-span-2">
          <div className="space-y-2">
            <Label>Transition par défaut entre les médias</Label>
            <Select value={value.transition || "none"} onValueChange={(v) => set({ transition: v as any })} disabled={disabled}>
              <SelectTrigger data-testid="config-transition-select" className="border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
              <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value="none">Aucune (coupure franche)</SelectItem><SelectItem value="fade">Fondu</SelectItem><SelectItem value="slide">Glissement</SelectItem><SelectItem value="zoom">Zoom</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Durée de la transition ({value.transition_ms} ms)</Label>
            <input type="range" min={100} max={3000} step={100} disabled={disabled || value.transition === "none"} data-testid="config-transition-ms" value={value.transition_ms} onChange={(e) => set({ transition_ms: Number(e.target.value) })} className="mt-3 w-full accent-orange-500" />
          </div>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Orientation de l'affichage</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="config-orientation">
            {ORIENTATIONS.map((o) => {
              const active = (value.orientation || "landscape") === o.v;
              const portrait = o.deg % 180 !== 0;
              return (
                <button key={o.v} type="button" disabled={disabled} data-testid={`config-orientation-${o.v}`} onClick={() => set({ orientation: o.v })}
                  className={`flex flex-col items-center gap-2 rounded-lg border px-3 py-3 text-xs transition-colors ${active ? "border-orange-500/60 bg-orange-500/10 text-orange-200" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                  <span className={`flex items-start justify-center rounded-sm border-2 ${active ? "border-orange-400" : "border-slate-500"} ${portrait ? "h-9 w-6" : "h-6 w-9"}`} style={{ transform: o.deg >= 180 ? "rotate(180deg)" : undefined }}>
                    <span className={`mt-0.5 h-1 w-1 rounded-full ${active ? "bg-orange-400" : "bg-slate-500"}`} />
                  </span>
                  <span>{o.label}</span><span className="font-mono text-[10px] text-slate-500">{o.deg}°</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500">Rotation du contenu dans le sens horaire. Si l'image apparaît à l'envers, choisissez la version inversée.</p>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3 md:col-span-2">
          <div><p className="text-sm font-medium text-slate-200">Afficher l'horloge</p><p className="text-xs text-slate-500">Superposée en coin d'écran sur la TV.</p></div>
          <div className="flex items-center gap-3">
            {value.clock && (
              <Select value={value.clock_style} onValueChange={(v) => set({ clock_style: v as any })} disabled={disabled}>
                <SelectTrigger data-testid="config-clock-style" className="h-9 w-40 border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value="digital_24h">Numérique 24 h</SelectItem><SelectItem value="digital_12h">Numérique 12 h</SelectItem><SelectItem value="analog">Analogique</SelectItem></SelectContent>
              </Select>
            )}
            <Switch disabled={disabled} data-testid="config-clock-switch" checked={value.clock} onCheckedChange={(c) => set({ clock: c })} />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div><h3 className="text-lg font-medium text-slate-100">Disposition de l'écran</h3><p className="text-xs text-slate-500">Partagez l'écran en zones : par exemple une vidéo, la météo et un flux RSS en même temps. Chaque zone joue sa propre liste en boucle.</p></div>
        <LayoutEditor value={value.layout} onChange={(l) => set({ layout: l })} playlists={playlists} disabled={disabled} />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div><h3 className="text-lg font-medium text-slate-100">Planification horaire</h3><p className="text-xs text-slate-500">Plages par jour (1 = lundi … 7 = dimanche). En cas de chevauchement, la priorité la plus haute l'emporte, puis l'ordre.</p></div>
          <Button type="button" variant="outline" size="sm" disabled={disabled || !playlists.length} data-testid="schedule-add-slot" onClick={addSlot} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"><Plus className="mr-1 h-4 w-4" />Ajouter une plage</Button>
        </div>
        {value.schedules.length === 0 && <p className="rounded-lg border border-dashed border-slate-800 p-4 text-sm text-slate-500">Aucune plage : la liste par défaut joue en continu.</p>}
        {value.schedules.map((s, i) => (
          <SlotRow key={i} index={i} slot={s} playlists={playlists} disabled={disabled} onChange={(n) => setSlot(i, n)} onRemove={() => set({ schedules: value.schedules.filter((_, j) => j !== i) })} />
        ))}
        {value.schedules.length > 0 && <ScheduleTimeline schedules={value.schedules} playlists={playlists} />}
      </div>

      <div className="space-y-4">
        <div><h3 className="text-lg font-medium text-slate-100">Horaire d'allumage de la télé</h3><p className="text-xs text-slate-500">Optionnel. Une plage peut passer minuit (ex. 18:00 → 02:00).</p></div>
        <PowerScheduleEditor value={value.power || { enabled: false, slots: [] }} onChange={(p) => set({ power: p })} disabled={disabled} />
      </div>
    </div>
  );
}
